import { useState } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import {
  ShoppingBag,
  X,
  Plus,
  Minus,
  Trash2,
  ShieldCheck,
  ArrowRight,
  CheckCircle2,
  Loader2,
  Lock,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { useAuth } from '@/providers/auth-provider'
import { appRoutes } from '@/constants'
import {
  useCart,
  useUpdateCartQuantity,
  useRemoveCartItem,
  useCheckout,
} from '../hooks/use-cart'

interface CartDrawerProps {
  isOpen: boolean
  onClose: () => void
}

export function CartDrawer({ isOpen, onClose }: CartDrawerProps) {
  const { user, isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  const { data: cart, isLoading } = useCart()
  const updateQuantity = useUpdateCartQuantity()
  const removeItem = useRemoveCartItem()
  const checkout = useCheckout()

  // Checkout step state: 'cart' | 'checkout' | 'success'
  const [step, setStep] = useState<'cart' | 'checkout' | 'success'>('cart')
  const [lastOrderNumber, setLastOrderNumber] = useState('')

  // Shipping form state
  const [fullName, setFullName] = useState(user ? `${user.firstName} ${user.lastName}` : '')
  const [phone, setPhone] = useState(user?.phone || '')
  const [addressLine1, setAddressLine1] = useState('')
  const [city, setCity] = useState('')
  const [state, setState] = useState('')
  const [postalCode, setPostalCode] = useState('')

  if (!isOpen) return null

  const items = cart?.items || []
  const totalAmount = cart?.totalAmount || 0
  const shippingCharge = totalAmount > 0 ? (totalAmount > 50000 ? 0 : 500) : 0
  const grandTotal = totalAmount + shippingCharge

  const handlePlaceOrder = (e: React.FormEvent) => {
    e.preventDefault()
    if (!fullName || !phone || !addressLine1 || !city || !postalCode) return

    checkout.mutate(
      {
        shippingAddress: {
          fullName,
          phone,
          addressLine1,
          city,
          state,
          postalCode,
        },
        paymentMethod: 'ONLINE_NETBANKING',
      },
      {
        onSuccess: (res: any) => {
          setLastOrderNumber(res?.data?.orderNumber || 'EAC-ORDER-CONFIRMED')
          setStep('success')
        },
      },
    )
  }

  const handleLogin = () => {
    onClose()
    navigate(appRoutes.auth.login, { state: { from: location.pathname } })
  }

  return (
    <div className="fixed inset-0 z-50 overflow-hidden animate-in fade-in duration-200">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-background/80 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      <div className="fixed inset-y-0 right-0 max-w-full flex pl-10">
        <div className="w-screen max-w-md bg-card/95 backdrop-blur-2xl border-l border-border/80 shadow-2xl flex flex-col">
          {/* Header */}
          <div className="p-5 border-b border-border/60 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="size-9 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
                <ShoppingBag className="size-4" />
              </div>
              <div>
                <h3 className="font-display font-bold text-base text-foreground">
                  {step === 'checkout'
                    ? 'Shipping & Checkout'
                    : step === 'success'
                    ? 'Order Confirmed'
                    : 'Shopping Cart'}
                </h3>
                {step === 'cart' && (
                  <p className="text-[11px] text-muted-foreground">
                    {cart?.totalCount || 0} items in cart
                  </p>
                )}
              </div>
            </div>

            <Button
              size="sm"
              variant="ghost"
              onClick={onClose}
              className="size-8 p-0 rounded-full hover:bg-muted"
            >
              <X className="size-4" />
            </Button>
          </div>

          {/* Not logged in */}
          {!isAuthenticated ? (
            <div className="flex-1 flex flex-col items-center justify-center p-8 text-center space-y-4">
              <div className="size-16 rounded-3xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
                <Lock className="size-8" />
              </div>
              <div>
                <h4 className="font-display font-bold text-lg text-foreground">Sign In to View Cart</h4>
                <p className="text-xs text-muted-foreground mt-1 max-w-xs">
                  Sign in to your account to add items, save your cart, and place vehicle parts orders with buyer protection.
                </p>
              </div>
              <Button
                onClick={handleLogin}
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-bold glow-md"
              >
                Sign In &amp; Continue
              </Button>
            </div>
          ) : step === 'success' ? (
            /* Order Success View */
            <div className="flex-1 flex flex-col items-center justify-center p-8 text-center space-y-5 animate-in zoom-in-95 duration-200">
              <div className="size-20 rounded-full bg-emerald-500/10 border-2 border-emerald-500/30 flex items-center justify-center text-emerald-500">
                <CheckCircle2 className="size-10" />
              </div>

              <div className="space-y-1.5">
                <Badge variant="outline" className="border-emerald-500/30 text-emerald-500 font-mono text-xs">
                  {lastOrderNumber}
                </Badge>
                <h3 className="font-display font-bold text-xl text-foreground">Order Successfully Placed!</h3>
                <p className="text-xs text-muted-foreground max-w-xs">
                  Thank you! Your genuine parts order has been confirmed. You will receive real-time courier tracking updates.
                </p>
              </div>

              <div className="w-full pt-4 space-y-2">
                <Button
                  onClick={() => {
                    setStep('cart')
                    onClose()
                  }}
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-bold"
                >
                  Continue Browsing Parts
                </Button>
              </div>
            </div>
          ) : step === 'checkout' ? (
            /* Checkout Shipping Form */
            <form onSubmit={handlePlaceOrder} className="flex-1 flex flex-col overflow-y-auto">
              <div className="p-5 space-y-4 flex-1">
                <div className="p-3.5 rounded-2xl bg-muted/40 border border-border/60 flex items-center justify-between">
                  <span className="text-xs text-muted-foreground font-medium">Order Total to Pay:</span>
                  <span className="font-display font-extrabold text-base text-primary">
                    ₹{grandTotal.toLocaleString('en-IN')}
                  </span>
                </div>

                <div className="space-y-3">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                    Delivery Address
                  </h4>

                  <div>
                    <label className="text-[10px] uppercase font-bold text-muted-foreground block mb-1">
                      Recipient Full Name *
                    </label>
                    <Input
                      required
                      placeholder="e.g. John Doe"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      className="text-xs rounded-xl h-9"
                    />
                  </div>

                  <div>
                    <label className="text-[10px] uppercase font-bold text-muted-foreground block mb-1">
                      Phone Number *
                    </label>
                    <Input
                      required
                      placeholder="+91 9876543210"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className="text-xs rounded-xl h-9"
                    />
                  </div>

                  <div>
                    <label className="text-[10px] uppercase font-bold text-muted-foreground block mb-1">
                      Address (House / Street) *
                    </label>
                    <Input
                      required
                      placeholder="Plot 42, Speed Avenue, Tech Park"
                      value={addressLine1}
                      onChange={(e) => setAddressLine1(e.target.value)}
                      className="text-xs rounded-xl h-9"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[10px] uppercase font-bold text-muted-foreground block mb-1">
                        City *
                      </label>
                      <Input
                        required
                        placeholder="Mumbai"
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                        className="text-xs rounded-xl h-9"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] uppercase font-bold text-muted-foreground block mb-1">
                        PIN Code *
                      </label>
                      <Input
                        required
                        placeholder="400001"
                        value={postalCode}
                        onChange={(e) => setPostalCode(e.target.value)}
                        className="text-xs rounded-xl h-9"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-[10px] uppercase font-bold text-muted-foreground block mb-1">
                      State
                    </label>
                    <Input
                      placeholder="Maharashtra"
                      value={state}
                      onChange={(e) => setState(e.target.value)}
                      className="text-xs rounded-xl h-9"
                    />
                  </div>
                </div>
              </div>

              {/* Bottom Place Order Button */}
              <div className="p-5 border-t border-border/80 bg-background/80 space-y-2">
                <Button
                  type="submit"
                  disabled={checkout.isPending}
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-bold h-11 rounded-2xl glow-md"
                >
                  {checkout.isPending ? (
                    <>
                      <Loader2 className="size-4 animate-spin mr-2" />
                      Processing Order...
                    </>
                  ) : (
                    <>
                      <span>Confirm &amp; Place Order</span>
                      <ArrowRight className="size-4 ml-2" />
                    </>
                  )}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setStep('cart')}
                  className="w-full text-xs text-muted-foreground hover:text-foreground"
                >
                  Back to Cart
                </Button>
              </div>
            </form>
          ) : (
            /* Cart Items View */
            <>
              <div className="flex-1 overflow-y-auto p-5 space-y-3">
                {isLoading ? (
                  <div className="h-40 flex items-center justify-center text-xs text-muted-foreground gap-2">
                    <Loader2 className="size-4 animate-spin text-primary" /> Loading cart...
                  </div>
                ) : items.length === 0 ? (
                  <div className="h-64 flex flex-col items-center justify-center text-center p-6 text-muted-foreground space-y-3">
                    <ShoppingBag className="size-12 opacity-30 text-primary" />
                    <p className="font-bold text-sm text-foreground">Your cart is empty</p>
                    <p className="text-xs max-w-xs">
                      Explore our OEM &amp; high-performance parts catalog and add components to your build.
                    </p>
                    <Link to={appRoutes.parts} onClick={onClose}>
                      <Button size="sm" variant="outline" className="border-border text-xs mt-2">
                        Browse Parts Catalog
                      </Button>
                    </Link>
                  </div>
                ) : (
                  items.map((item) => {
                    let partImg = 'https://images.unsplash.com/photo-1486006920555-c77dce18193b?auto=format&fit=crop&w=200&q=80'
                    if (Array.isArray(item.part.images) && item.part.images.length > 0) {
                      const first = item.part.images[0]
                      partImg = typeof first === 'string' ? first : first?.url || partImg
                    }

                    return (
                      <div
                        key={item.id}
                        className="p-3.5 rounded-2xl bg-muted/30 border border-border/60 flex items-center gap-3 group"
                      >
                        <img
                          src={partImg}
                          alt={item.part.title}
                          className="size-16 rounded-xl object-cover border border-border/80 shrink-0"
                        />

                        <div className="min-w-0 flex-1">
                          <p className="font-bold text-xs text-foreground truncate">
                            {item.part.title}
                          </p>
                          <p className="text-[10px] text-muted-foreground font-mono">
                            {item.part.partNumber}
                          </p>
                          <p className="font-display font-extrabold text-xs text-primary mt-1">
                            ₹{Number(item.part.price).toLocaleString('en-IN')}
                          </p>

                          {/* Quantity Controls */}
                          <div className="flex items-center gap-2 mt-2">
                            <div className="flex items-center border border-border rounded-lg bg-card">
                              <button
                                type="button"
                                onClick={() =>
                                  updateQuantity.mutate({ id: item.id, quantity: item.quantity - 1 })
                                }
                                disabled={updateQuantity.isPending}
                                className="size-6 flex items-center justify-center text-muted-foreground hover:text-foreground disabled:opacity-50"
                              >
                                <Minus className="size-3" />
                              </button>
                              <span className="px-2 text-xs font-bold text-foreground">
                                {item.quantity}
                              </span>
                              <button
                                type="button"
                                onClick={() =>
                                  updateQuantity.mutate({ id: item.id, quantity: item.quantity + 1 })
                                }
                                disabled={updateQuantity.isPending || item.quantity >= item.part.stockCount}
                                className="size-6 flex items-center justify-center text-muted-foreground hover:text-foreground disabled:opacity-50"
                              >
                                <Plus className="size-3" />
                              </button>
                            </div>

                            <button
                              type="button"
                              onClick={() => removeItem.mutate(item.id)}
                              className="text-muted-foreground hover:text-red-500 p-1 text-xs transition-colors ml-auto"
                              title="Remove item"
                            >
                              <Trash2 className="size-3.5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    )
                  })
                )}
              </div>

              {/* Bottom Price Summary & Checkout Action */}
              {items.length > 0 && (
                <div className="p-5 border-t border-border/80 bg-background/80 space-y-3">
                  <div className="space-y-1.5 text-xs">
                    <div className="flex justify-between text-muted-foreground">
                      <span>Subtotal</span>
                      <span className="font-semibold text-foreground">
                        ₹{totalAmount.toLocaleString('en-IN')}
                      </span>
                    </div>
                    <div className="flex justify-between text-muted-foreground">
                      <span>Estimated Shipping</span>
                      <span className="font-semibold text-foreground">
                        {shippingCharge === 0 ? 'FREE Express' : `₹${shippingCharge}`}
                      </span>
                    </div>
                    <div className="border-t border-border/60 pt-2 flex justify-between font-bold text-sm text-foreground">
                      <span>Total Amount</span>
                      <span className="text-primary font-display font-extrabold text-base">
                        ₹{grandTotal.toLocaleString('en-IN')}
                      </span>
                    </div>
                  </div>

                  <Button
                    onClick={() => setStep('checkout')}
                    className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-bold h-11 rounded-2xl glow-md gap-2"
                  >
                    <span>Proceed to Checkout</span>
                    <ArrowRight className="size-4" />
                  </Button>

                  <div className="flex items-center justify-center gap-1.5 text-[10px] text-muted-foreground">
                    <ShieldCheck className="size-3.5 text-emerald-500" />
                    <span>100% Genuine Certified Parts • Secure Gateway</span>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
